import type { ReactNode } from 'react';

import { formatDate, toMachineDate } from '@shared/format';
import { DTable, DTableBody, DTableHead, DTableRowHeader, DTableScroll } from '@shared/ui/dtable';
import { ArrowDownIcon, ArrowUpIcon, EditIcon, TrashIcon } from '@shared/ui/icons';
import { RowMenu, type RowMenuEntry } from '@shared/ui/rowmenu';

import { actionLabels, unpublishCopy } from '../_shared/copy';
import { PostStateBadge } from '../_shared/PostStateBadge';
import { REVIEWER_ACTIONS, STATE_ACTIONS, withdrawUnpublishes, type PostAction } from '../_shared/states';
import type { EditorialPostRow } from '../_shared/types';
import { copy } from './copy';

export const TABLE_COLUMNS = 5;

/**
 * Five columns — المقال over its tag line · الحالة · الكاتب · تاريخ النشر · the
 * row menu. There is no sixth for a topic: a post carries none, and the earlier
 * pass that drew one was printing the same word twice per row.
 *
 * **This table sorts and the shelf table does not, and the difference is what
 * the column means.** A shelf's rank is a field on the shelf, so an arrow there
 * would promise a re-ordering the surface does not own. A post has a
 * publication date and no rank, so the sort is a view the reader chooses and it
 * writes nothing.
 *
 * **The comparison reads the machine value, never the rendered «قبل يومين».**
 * A post with no date holds the tail in both directions: ordering something by
 * a value it does not have is a claim either way.
 */
export function PostTable({
  rows,
  direction,
  onToggleSort,
  canReview,
  onAction,
}: {
  rows: readonly EditorialPostRow[];
  direction: 'ascending' | 'descending';
  onToggleSort: () => void;
  /** The granted reviewer role — the two decisions are absent without it. */
  canReview: boolean;
  onAction: (action: PostAction, post: EditorialPostRow) => void;
}) {
  return (
    <DTableScroll>
      <DTable caption={copy.section.caption}>
        <DTableHead>
          <tr>
            <th scope="col">{copy.columns.post}</th>
            <th scope="col">{copy.columns.state}</th>
            <th scope="col">{copy.columns.author}</th>
            <th scope="col" aria-sort={direction}>
              <button
                type="button"
                onClick={onToggleSort}
                className="flex items-center gap-2 rounded-sm text-xs font-semibold text-ink-muted hover:text-ink"
              >
                <span>{copy.columns.date}</span>
                {direction === 'descending' ? <ArrowDownIcon /> : <ArrowUpIcon />}
                <span className="sr-only">
                  {direction === 'descending' ? copy.sort.descending : copy.sort.ascending}
                </span>
              </button>
            </th>
            <th scope="col" className="[inline-size:3.5rem]">
              <span className="sr-only">{copy.columns.menu}</span>
            </th>
          </tr>
        </DTableHead>

        <DTableBody>
          {rows.map((post) => (
            <tr key={post.id}>
              <DTableRowHeader>
                <span className="block">{post.title}</span>
                <span className="block text-xs text-ink-muted">
                  {post.tags.length > 0 ? post.tags.map((tag) => tag.name).join(' · ') : copy.row.noTags}
                </span>
              </DTableRowHeader>

              <td>
                <PostStateBadge state={post.state} />
              </td>

              <td>
                <bdi>{post.author.displayName}</bdi>
              </td>

              <td className="whitespace-nowrap">
                {post.publishedAt ? (
                  <time dateTime={toMachineDate(post.publishedAt)}>{formatDate(post.publishedAt)}</time>
                ) : (
                  <span className="text-ink-muted">{copy.row.notPublished}</span>
                )}
              </td>

              <td className="whitespace-nowrap [inline-size:3.5rem]">
                <RowMenu
                  label={copy.row.menuLabel(post.title)}
                  entries={menuFor(post, canReview, onAction)}
                />
              </td>
            </tr>
          ))}
        </DTableBody>
      </DTable>
    </DTableScroll>
  );
}

const ACTION_ICONS: Partial<Record<PostAction, ReactNode>> = {
  edit: <EditIcon className="flex-none" />,
  delete: <TrashIcon className="flex-none" />,
};

/**
 * What one row offers.
 *
 * The set is derived from the row's **own state**, which is the same value the
 * badge renders — one source of truth, so what the menu offers cannot drift
 * from what the row shows. The two decisions drop out entirely for a caller
 * without the reviewer grant: absent, never disabled, because a disabled
 * control promises a permission that is coming and this one is not.
 */
function menuFor(
  post: EditorialPostRow,
  canReview: boolean,
  onAction: (action: PostAction, post: EditorialPostRow) => void,
): RowMenuEntry[] {
  return STATE_ACTIONS[post.state]
    .filter((action) => canReview || !REVIEWER_ACTIONS.includes(action))
    .map((action) => ({
      key: `${post.id}:${action}`,
      // One transition, two labels: withdrawing an article that readers can see
      // is an unpublish, and the entry says so rather than naming the state it
      // lands in.
      label:
        action === 'withdraw' && withdrawUnpublishes(post.state) ? unpublishCopy.menu : actionLabels[action],
      icon: ACTION_ICONS[action],
      danger: action === 'delete',
      onSelect: () => onAction(action, post),
    }));
}

/** The row menu's «افتح المقال» is a navigation; the surface routes it. */
export function postHref(post: EditorialPostRow): string {
  return `/editorial/${post.id}`;
}
